// Station-level detail of ONE analysis, drawn the way its report draws it:
//   SSD / PSD  dots       red = deficient, green = passes
//   GRW        dots       red = guardrail warranted, green = not warranted
//   GLR        dots       the glare report's six impact bands
//   FSA        triangles  pointing downhill; flat spots solid blue
//   ISD        rays       eye -> target; red = target not visible, green = visible
// Up to three analyses are drawn together: each keeps its own colours, gets its own
// SHAPE (SSD circle, PSD square, GRW diamond) and sits a few pixels beside the others,
// so red-on-red from two analyses at one station never reads as one mark.
// An order carries ~150k stations per analysis, far beyond what SVG or React can
// hold, so this is ONE imperative Leaflet layer painting a canvas. The pane ignores
// the mouse; a map click picks the nearest drawn station instead and opens a popup
// with that station's own report link (`<report>#m=<anchor>`). Clicks on a deficiency
// run never get here: the run's own popup stops them.
//
// layers: [{analysis, chunks: [{rows, report, label}]}] — one chunk per corridor and
// direction, because the report page differs for each.
import L from 'leaflet';
import { createLayerComponent } from '@react-leaflet/core';

export const GLR_BANDS = [
  ['≤120 h', '#EEFC53'], ['120–130 h', '#FCD753'], ['130–140 h', '#FCB653'],
  ['140–150 h', '#F57F45'], ['150–160 h', '#F55F45'], ['≥160 h', '#8A2917'],
];
export const DETAIL_COLORS = { ok: '#2f9e44', bad: '#e03131', flat: '#1d4ed8', slope: '#74c0fc' };
export const PATH_COLOR = '#c4b5fd';
export const DETAIL_ANALYSES = ['SSD', 'PSD', 'GRW', 'GLR', 'FSA', 'ISD'];
export const MAX_DETAIL_ANALYSES = 3;
export const SHAPES = { SSD: 'circle', PSD: 'square', GRW: 'diamond', GLR: 'circle' };
const PANE = 'ovStations';
const PICK_PX = 10;

const STATUS = {
  SSD: ['Passes', 'Short of the stopping sight distance'], PSD: ['Passing allowed', 'No-pass station'],
  GRW: ['Guardrail not warranted', 'Guardrail warranted'], FSA: ['Drains', 'Flat spot'],
};
const esc = (t) => String(t == null ? '' : t).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

/** Popup markup of one station (exported for the tests). */
export function stationPopupHtml(analysis, row, chunk) {
  const anchor = analysis === 'ISD' ? null : row[row.length - 1];
  const status = analysis === 'GLR' ? `${GLR_BANDS[row[2]][0]} of glare per year` : (STATUS[analysis] || [])[row[2]] || '';
  const href = chunk.report && anchor != null ? `${chunk.report}#m=${anchor}` : null;
  return `<div class="safety-popup"><strong>${esc(analysis)} station</strong>`
    + `<div class="safety-popup-mp">${esc(chunk.label || '')}${anchor != null ? ` · mile ${esc(anchor)}` : ''}</div>`
    + `<table><tbody><tr><td>Result</td><td>${esc(status)}</td></tr></tbody></table>`
    + (href ? `<div class="safety-popup-actions"><a class="safety-btn safety-report-btn" href="${esc(href)}" target="_blank" rel="noreferrer">Open ${esc(analysis)} report at this station ↗</a></div>`
      : '<div class="safety-popup-mp">No online report for this station.</div>')
    + '</div>';
}

const strideAt = (z) => (z >= 15 ? 1 : z >= 14 ? 2 : z >= 13 ? 3 : z >= 12 ? 6 : z >= 11 ? 10 : 20);
const radiusAt = (z) => (z >= 15 ? 3.6 : z >= 13 ? 2.7 : z >= 11 ? 2 : 1.5);

const CanvasStations = L.Layer.extend({
  initialize(options) {
    L.setOptions(this, options);
    this._layers = [];          // [{analysis, rows}]
  },
  onAdd(map) {
    let pane = map.getPane(PANE);
    if (!pane) {
      pane = map.createPane(PANE);
      pane.style.zIndex = 415;
      pane.style.pointerEvents = 'none';
    }
    this._canvas = L.DomUtil.create('canvas', 'leaflet-zoom-hide', pane);
    map.on('moveend zoomend resize', this._redraw, this);
    map.on('click', this._pick, this);
    this._redraw();
  },
  onRemove(map) {
    map.off('moveend zoomend resize', this._redraw, this);
    map.off('click', this._pick, this);
    if (this._canvas) L.DomUtil.remove(this._canvas);
    this._canvas = null;
  },
  setData(layers) {
    this._layers = (layers || []).map((l) => ({ ...l, chunks: (l.chunks || []).filter((c) => c.rows && c.rows.length) }))
      .filter((l) => l.chunks.length);
    if (this._map) this._redraw();
  },
  _shift(index, r) {
    const count = this._layers.length;
    return count > 1 ? (index - (count - 1) / 2) * (2 * r + 1.5) * 0.75 : 0;
  },
  // Nearest drawn station to the click (flagged ones win a tie), within PICK_PX.
  _pick(e) {
    const map = this._map;
    if (!map || !this._layers.length) return;
    const click = e.containerPoint;
    const z = map.getZoom();
    const stride = strideAt(z);
    const r = radiusAt(z);
    const bounds = map.getBounds();
    let best = null;
    this._layers.forEach((layer, index) => {
      if (layer.analysis === 'ISD') return;                  // rays: the intersection markers carry the ISD links
      const shift = this._shift(index, r);
      layer.chunks.forEach((chunk) => {
        const top = layer.analysis === 'GLR' ? GLR_BANDS.length - 1 : 1;
        for (let i = 0; i < chunk.rows.length; i += 1) {
          const row = chunk.rows[i];
          const flagged = row[2] === top;
          if (!flagged && i % stride !== 0) continue;        // eslint-disable-line no-continue
          if (!bounds.contains([row[0], row[1]])) continue;  // eslint-disable-line no-continue
          const p = map.latLngToContainerPoint([row[0], row[1]]);
          const d = Math.hypot(p.x + shift - click.x, p.y - shift - click.y) - (flagged ? 1.5 : 0);
          if (d <= PICK_PX && (!best || d < best.d)) best = { d, row, chunk, analysis: layer.analysis };
        }
      });
    });
    if (best) {
      L.popup({ maxWidth: 300 }).setLatLng([best.row[0], best.row[1]])
        .setContent(stationPopupHtml(best.analysis, best.row, best.chunk)).openOn(map);
    }
  },
  _redraw() {
    const map = this._map;
    const canvas = this._canvas;
    if (!map || !canvas) return;
    const size = map.getSize();
    const ratio = window.devicePixelRatio || 1;
    L.DomUtil.setPosition(canvas, map.containerPointToLayerPoint([0, 0]));
    canvas.width = size.x * ratio;
    canvas.height = size.y * ratio;
    canvas.style.width = `${size.x}px`;
    canvas.style.height = `${size.y}px`;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, size.x, size.y);
    const bounds = map.getBounds().pad(0.05);
    const south = bounds.getSouth();
    const north = bounds.getNorth();
    const west = bounds.getWest();
    const east = bounds.getEast();
    const z = map.getZoom();
    const stride = strideAt(z);
    const r = radiusAt(z);
    const inView = (row) => row[0] >= south && row[0] <= north && row[1] >= west && row[1] <= east;
    this._layers.forEach((layer, index) => layer.chunks.forEach((chunk) => {
    const a = layer.analysis;
    const rows = chunk.rows;
    // side-by-side: a constant diagonal screen offset per analysis (none when alone)
    const shift = this._shift(index, r);
    const at = (lat, lng) => {
      const p = map.latLngToContainerPoint([lat, lng]);
      return { x: p.x + shift, y: p.y - shift };
    };
    const shape = SHAPES[a] || 'circle';

    const dot = (row, color) => {
      const p = at(row[0], row[1]);
      ctx.beginPath();
      if (shape === 'square') ctx.rect(p.x - r, p.y - r, 2 * r, 2 * r);
      else if (shape === 'diamond') {
        const d = r * 1.35;
        ctx.moveTo(p.x, p.y - d); ctx.lineTo(p.x + d, p.y); ctx.lineTo(p.x, p.y + d); ctx.lineTo(p.x - d, p.y); ctx.closePath();
      } else ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    };
    // Two passes so the rows that matter (flag = 1 / top band) are never painted over;
    // the ordinary rows are thinned by zoom, the flagged ones never are.
    const passes = (isFlagged, drawPlain, drawFlagged) => {
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (!isFlagged(row) && i % stride === 0 && inView(row)) drawPlain(row);
      }
      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        if (isFlagged(row) && inView(row)) drawFlagged(row);
      }
    };

    if (a === 'GLR') {
      const top = GLR_BANDS.length - 1;
      passes((row) => row[2] === top, (row) => dot(row, GLR_BANDS[row[2]][1]), (row) => dot(row, GLR_BANDS[top][1]));
    } else if (a === 'FSA') {
      const s = r * 2.1;
      const triangle = (row, fill, edge) => {
        if (row[3] == null) { dot(row, fill); return; }
        const p = at(row[0], row[1]);
        const t = (row[3] * Math.PI) / 180;        // bearing: 0 = north = up the screen, clockwise
        const ux = Math.sin(t);
        const uy = -Math.cos(t);
        ctx.beginPath();
        ctx.moveTo(p.x + ux * s, p.y + uy * s);
        ctx.lineTo(p.x - ux * s * 0.6 - uy * s * 0.62, p.y - uy * s * 0.6 + ux * s * 0.62);
        ctx.lineTo(p.x - ux * s * 0.6 + uy * s * 0.62, p.y - uy * s * 0.6 - ux * s * 0.62);
        ctx.closePath();
        ctx.fillStyle = fill;
        ctx.fill();
        if (edge) { ctx.strokeStyle = edge; ctx.lineWidth = 0.8; ctx.stroke(); }
      };
      passes((row) => row[2] === 1, (row) => triangle(row, DETAIL_COLORS.slope, null), (row) => triangle(row, DETAIL_COLORS.flat, '#ffffff'));
    } else if (a === 'ISD') {
      const ray = (row, color, width, alpha) => {
        const p = at(row[0], row[1]);
        const q = at(row[3], row[4]);
        ctx.globalAlpha = alpha;
        ctx.beginPath();
        ctx.moveTo(p.x, p.y);
        ctx.lineTo(q.x, q.y);
        ctx.strokeStyle = color;
        ctx.lineWidth = width;
        ctx.stroke();
        ctx.globalAlpha = 1;
      };
      passes((row) => row[2] === 1, (row) => ray(row, DETAIL_COLORS.ok, 1, 0.45), (row) => ray(row, DETAIL_COLORS.bad, 1.6, 0.9));
    } else {
      passes((row) => row[2] === 1, (row) => dot(row, DETAIL_COLORS.ok), (row) => dot(row, DETAIL_COLORS.bad));
    }
    }));
  },
});

const StationsLayer = createLayerComponent(
  ({ layers, ...options }, context) => {
    const instance = new CanvasStations(options);
    instance.setData(layers);
    return { instance, context };
  },
  (instance, props, prev) => {
    if (props.layers !== prev.layers) instance.setData(props.layers);
  },
);

export default StationsLayer;
